import { useTranslation } from 'react-i18next';
import { StateView } from '../../components/patterns/index.ts';
import { Button, PageHeader, Panel } from '../../components/ui.tsx';
import type { SampleDomain } from '../samples/index.ts';
import { BoardScreen, DashboardScreen, ListScreen, RecordScreen } from './ScreensMain.tsx';
import {
  AgendaScreen,
  ConversationScreen,
  DocumentScreen,
  GalleryScreen,
  ReportScreen,
  SettingsScreen,
  SignInScreen,
  WizardScreen,
} from './ScreensMore.tsx';

/** As telas da bancada, na ordem do catálogo de padrões de tela (packages/shared/src/styles/patterns.ts). */
export const PREVIEW_SCREENS = [
  'dashboard',
  'list',
  'record',
  'board',
  'agenda',
  'gallery',
  'wizard',
  'conversation',
  'report',
  'settings',
  'document',
  'login',
] as const;
export type PreviewScreen = (typeof PREVIEW_SCREENS)[number];

export const PREVIEW_STATES = ['normal', 'empty', 'loading', 'error', 'denied'] as const;
export type PreviewState = (typeof PREVIEW_STATES)[number];

/**
 * A tela de exemplo escolhida na barra da prévia, com os dados do exemplo de negócio. Num estado
 * que não é o normal, o corpo da tela dá lugar ao estado (vazia, carregando, com erro, sem
 * permissão), como a tela de verdade faz.
 */
export function ScreenView({
  screen,
  d,
  state,
}: {
  screen: PreviewScreen;
  d: SampleDomain;
  state: PreviewState;
}) {
  const { t } = useTranslation();
  if (state !== 'normal' && screen !== 'login' && screen !== 'document') {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={d.list.title} description={d.list.sub} />
        <Panel>
          <StateView
            kind={state}
            title={
              state === 'empty'
                ? t('style.demo.emptyTitle', { what: d.mainChild.toLowerCase() })
                : undefined
            }
            action={
              state === 'empty' ? (
                <Button>{d.list.newL}</Button>
              ) : state === 'error' ? (
                <Button variant="secondary">{t('style.demo.retry')}</Button>
              ) : undefined
            }
          />
        </Panel>
      </div>
    );
  }
  switch (screen) {
    case 'dashboard':
      return <DashboardScreen d={d} />;
    case 'list':
      return <ListScreen d={d} />;
    case 'record':
      return <RecordScreen d={d} />;
    case 'board':
      return <BoardScreen d={d} />;
    case 'agenda':
      return <AgendaScreen d={d} />;
    case 'gallery':
      return <GalleryScreen d={d} />;
    case 'wizard':
      return <WizardScreen d={d} />;
    case 'conversation':
      return <ConversationScreen d={d} />;
    case 'report':
      return <ReportScreen d={d} />;
    case 'settings':
      return <SettingsScreen d={d} />;
    case 'document':
      return <DocumentScreen d={d} />;
    default:
      return <SignInScreen d={d} />;
  }
}
